// Synthèse des stocks adaptée aux droits (charte D-038, « exceptions d'abord ») : ce qui demande une action en
// tête (alertes élevées et critiques, installations avec anomalies ou éléments en attente), puis réserves par
// produit (agrégats, part des installations à donnée récente, données périmées exclues et comptées à part),
// autonomie nationale et provinciale, installations de stock et, pour un distributeur, ses positions par dépôt.
// Le serveur ne renvoie que ce que l'utilisateur peut lire ; une section absente porte le motif de son absence.

import { Link, Navigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { formaterNombre } from '@carburanttrack/shared';
import { api } from '../../api/client';
import { useFiltres } from '../../app/filtres';
import { useSession } from '../../app/session';
import { Indisponible, MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { dateHeure, jourLong, libelleCategorie, libelleProvince } from '../../composants/format';
import { AutonomieJours, LienAction, PastilleFraicheur, quantite, TableauPositions, tonDeNiveau, useNomProduit } from './composants';
import type { Autonomie, LigneAgregat, SyntheseStocks } from './types';

export function useSynthese(province: string | null) {
  return useQuery({
    queryKey: ['stocks', 'synthese', province],
    queryFn: async () => (await api<SyntheseStocks>(`/api/v1/stocks/synthese${province ? `?province=${province}` : ''}`)).donnees,
  });
}

function CarteReserve({ l }: { l: LigneAgregat }) {
  const part = l.partRecentes === null ? null : Number(l.partRecentes);
  const exclues = l.exclues.filter((e) => e.motif === 'Donnée périmée').length;
  const sansMesure = l.exclues.length - exclues;
  return (
    <div className="carte-produit">
      <span className="nom">{l.produitNom}</span>
      <span className="grand">{quantite(l.total, l.unite)}</span>
      <small>
        {Number(l.transit ?? 0) !== 0 ? `dont transit ${quantite(l.transit, l.unite, l.total)} ; ` : ''}{formaterNombre(l.nombreRecentes)} installation{l.nombreRecentes > 1 ? 's' : ''} à donnée récente sur {formaterNombre(l.nombreInstallations)}
        {part !== null ? ` (${formaterNombre(part, 0)} %)` : ''}
      </small>
      {exclues + sansMesure > 0 && (
        <small>
          Exclues du total : {exclues > 0 ? `${exclues} à donnée périmée` : ''}
          {exclues > 0 && sansMesure > 0 ? ', ' : ''}
          {sansMesure > 0 ? `${sansMesure} sans mesure` : ''}
        </small>
      )}
    </div>
  );
}

export function PageStocks() {
  const { province } = useFiltres();
  const { etat } = useSession();
  const synthese = useSynthese(province);
  const nomProduit = useNomProduit();
  if (synthese.isLoading) return <Squelette lignes={8} libelle="Chargement des stocks" />;
  if (synthese.error) return <MessageErreur erreur={synthese.error} titre="Stocks indisponibles" />;
  const s = synthese.data!;
  const estDistributeur = etat?.profil === 'distributeur';
  // Un opérateur ou une station qui ne lit qu'une installation arrive directement sur sa fiche de stock.
  const operateur = etat?.profil === 'operateur' || etat?.profil === 'station';
  if (!estDistributeur && s.installations.length === 1 && (operateur || !s.agregats)) return <Navigate to={`/stocks/installations/${s.installations[0]!.id}`} replace />;

  const aTraiter = s.installations.filter((i) => i.anomaliesOuvertes > 0 || i.declarationsEnAttente > 0 || i.fraicheur === 'perimee');
  const autonomieNationale = (s.autonomie ?? []).filter((a) => a.portee === 'national');
  const autonomieProvinces = (s.autonomie ?? []).filter((a) => a.portee === 'province');
  const provincesAutonomie = [...new Set(autonomieProvinces.map((a) => a.provinceCode!))].sort();
  const produitsAutonomie = [...new Set(autonomieProvinces.map((a) => a.produitCode))].sort();
  const cellule = (a: Autonomie | undefined) => (a ? <AutonomieJours jours={a.jours} confiance={a.confiance} motif={a.motif} compact /> : <span className="cellule-indisponible">sans objet</span>);
  const titre = estDistributeur ? 'Mes stocks' : s.agregats ? 'Stocks et réserves' : 'Stocks';
  const perimetre = province ? libelleProvince(province) : s.agregats?.transitInclus ? 'périmètre national' : 'votre périmètre';

  return (
    <div className="page-stocks">
      <div className="page-tete">
        <div>
          <span className="contexte">
            {jourLong()}, {perimetre}, calculé le {dateHeure(s.maintenant)}
          </span>
          <h1>{titre}</h1>
          {s.agregats && <p>{s.agregats.base}.</p>}
        </div>
        <div className="actions">
          <Link className="bouton" to="/approvisionnements">
            Arrivages
          </Link>
          <Link className="bouton" to="/alertes">
            Alertes
          </Link>
        </div>
      </div>

      {(s.alertes?.length ?? 0) + aTraiter.length > 0 && (
        <section aria-labelledby="titre-a-traiter" className="espace-bas">
          <h2 id="titre-a-traiter" className="sur-titre">
            À traiter d’abord
          </h2>
          <ol className="liste-exceptions">
            {(s.alertes ?? []).map((a, i) => (
              <li key={a.id}>
                <Link className={`exception ${tonDeNiveau(a.niveau)}`} to={`/alertes/${a.id}`}>
                  <span className="numero" aria-hidden="true">
                    {i + 1}
                  </span>
                  <span className="texte">
                    <span className="marque-exception">Alerte {a.niveau === 'critique' ? 'critique' : 'élevée'}</span>
                    <b>{a.titre}</b>
                    <small>Détectée le {dateHeure(a.derniereDetection)}</small>
                  </span>
                  <Icone nom="chevron" />
                </Link>
              </li>
            ))}
            {aTraiter.slice(0, 6).map((i, k) => (
              <li key={i.id}>
                <Link className={`exception ${i.fraicheur === 'perimee' ? 'insuffisante' : 'ambre'}`} to={`/stocks/installations/${i.id}`}>
                  <span className="numero" aria-hidden="true">
                    {(s.alertes?.length ?? 0) + k + 1}
                  </span>
                  <span className="texte">
                    <span className="marque-exception">{libelleCategorie(i.categorie)}</span>
                    <b>{i.nom}</b>
                    <small>
                      {[
                        i.anomaliesOuvertes > 0 ? `${i.anomaliesOuvertes} anomalie${i.anomaliesOuvertes > 1 ? 's' : ''} à vérifier` : null,
                        i.declarationsEnAttente > 0 ? `${i.declarationsEnAttente} déclaration${i.declarationsEnAttente > 1 ? 's' : ''} en attente de validation` : null,
                        i.fraicheur === 'perimee' ? `dernière mesure périmée (${i.age})` : null,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </small>
                  </span>
                  <Icone nom="chevron" />
                </Link>
              </li>
            ))}
          </ol>
        </section>
      )}

      {s.agregats ? (
        <section className="carte-bloc" aria-labelledby="titre-reserves">
          <div className="carte-bloc-tete">
            <h2 id="titre-reserves">Réserves des dépôts par produit</h2>
            <span className="attenue">{s.agregats.transitInclus ? 'Transit national inclus (D-021)' : 'Transit national non inclus (périmètre provincial)'}</span>
          </div>
          {s.agregats.depots.length === 0 ? (
            <Indisponible motif="aucun jaugeage de dépôt dans le périmètre" />
          ) : (
            <div className="grille-produits">
              {s.agregats.depots.map((l) => (
                <CarteReserve key={`${l.produit}${l.unite}`} l={l} />
              ))}
            </div>
          )}
          <h3>Réseau des stations</h3>
          {s.agregats.stations.length === 0 ? (
            <Indisponible motif="aucune déclaration de station dans le périmètre" />
          ) : (
            <div className="tableau-conteneur">
              <table className="tableau">
                <caption className="visuellement-cache">Stocks déclarés des stations par produit</caption>
                <thead>
                  <tr>
                    <th scope="col">Produit</th>
                    <th scope="col" className="nombre">
                      Stock déclaré retenu
                    </th>
                    <th scope="col" className="nombre">
                      Stations à donnée récente
                    </th>
                    <th scope="col" className="nombre masquable">
                      Exclues (périmées ou sans mesure)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {s.agregats.stations.map((l) => {
                    return (
                      <tr key={`${l.produit}${l.unite}`}>
                        <td>{l.produitNom}</td>
                        <td className="nombre">{quantite(l.installations, l.unite)}</td>
                        <td className="nombre">
                          {formaterNombre(l.nombreRecentes)} sur {formaterNombre(l.nombreInstallations)}
                          {l.partRecentes !== null && <small>{formaterNombre(l.partRecentes, 0)} %</small>}
                        </td>
                        <td className="nombre masquable">{formaterNombre(l.exclues.length)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="legende-tableau">
            Une donnée périmée (plus de {s.agregats.seuilsFraicheur.stations.ancienneHeures} h) n’entre dans aucun total et est comptée à part. Une donnée récente a moins de {s.agregats.seuilsFraicheur.stations.recenteHeures} h.
          </p>
        </section>
      ) : (
        s.motifs.agregats && !estDistributeur && <p className="attenue">{s.motifs.agregats}.</p>
      )}

      {s.autonomie && (autonomieNationale.length > 0 || autonomieProvinces.length > 0) && (
        <section className="carte-bloc" aria-labelledby="titre-autonomie">
          <div className="carte-bloc-tete">
            <h2 id="titre-autonomie">Autonomie par produit</h2>
            <span className="attenue">Stock utilisable divisé par la consommation moyenne (D-027)</span>
          </div>
          {autonomieNationale.length > 0 && (
            <div className="grille-produits">
              {autonomieNationale.map((a) => (
                <div key={a.id} className="carte-produit">
                  <span className="nom">{nomProduit(a.produitCode)}, national</span>
                  <AutonomieJours jours={a.jours} confiance={a.confiance} motif={a.motif} joursAvecLivraisons={a.joursAvecLivraisons} />
                </div>
              ))}
            </div>
          )}
          {provincesAutonomie.length > 0 && (
            <div className="tableau-conteneur">
              <table className="tableau">
                <caption className="visuellement-cache">Autonomie par province et par produit</caption>
                <thead>
                  <tr>
                    <th scope="col">Province</th>
                    {produitsAutonomie.map((p) => (
                      <th key={p} scope="col">
                        {nomProduit(p)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {provincesAutonomie.map((code) => (
                    <tr key={code}>
                      <th scope="row">{libelleProvince(code)}</th>
                      {produitsAutonomie.map((p) => (
                        <td key={p}>{cellule(autonomieProvinces.find((a) => a.provinceCode === code && a.produitCode === p))}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {s.positions && s.positions.length > 0 && (
        <section className="carte-bloc" aria-labelledby="titre-positions">
          <div className="carte-bloc-tete">
            <h2 id="titre-positions">Mes positions par dépôt et produit</h2>
            <span className="attenue">Disponible à l’expédition : position moins réservé, immobilisé et sécurité (D-026)</span>
          </div>
          <TableauPositions positions={s.positions} avecInstallation avecProprietaire={false} legende="Positions par dépôt et produit" />
        </section>
      )}
      {estDistributeur && s.positions && s.positions.length === 0 && <p className="message info">Aucune position de stock en dépôt pour votre organisation.</p>}

      {s.transit && s.transit.length > 0 && (
        <section className="carte-bloc" aria-labelledby="titre-transit">
          <div className="carte-bloc-tete">
            <h2 id="titre-transit">En transit</h2>
            <span className="attenue">Chargé au dépôt, pas encore réceptionné : compté dans le physique national, jamais dans un site</span>
          </div>
          <div className="tableau-conteneur">
            <table className="tableau">
              <caption className="visuellement-cache">Lignes en transit</caption>
              <thead>
                <tr>
                  <th scope="col">Chargé le</th>
                  <th scope="col">Produit</th>
                  {!estDistributeur && <th scope="col">Propriétaire</th>}
                  <th scope="col" className="nombre">
                    Quantité
                  </th>
                </tr>
              </thead>
              <tbody>
                {s.transit.slice(0, 20).map((l) => (
                  <tr key={l.id}>
                    <td>{dateHeure(l.dateChargement)}</td>
                    <td>{nomProduit(l.produitCode)}</td>
                    {!estDistributeur && <td>{l.proprietaireNom}</td>}
                    <td className="nombre">{quantite(l.quantite, l.unite)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {s.installations.length > 0 && (
        <section className="carte-bloc" aria-labelledby="titre-installations">
          <div className="carte-bloc-tete">
            <h2 id="titre-installations">{estDistributeur ? 'Dépôts et stations' : 'Installations de stock'}</h2>
            <span className="attenue">{formaterNombre(s.installations.length)} lisibles avec vos habilitations</span>
          </div>
          <div className="tableau-conteneur">
            <table className="tableau">
              <caption className="visuellement-cache">Installations de stock</caption>
              <thead>
                <tr>
                  <th scope="col">Installation</th>
                  <th scope="col" className="masquable">
                    Province
                  </th>
                  <th scope="col">Dernière mesure</th>
                  <th scope="col" className="nombre">
                    À traiter
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...s.installations]
                  .sort((a, b) => b.anomaliesOuvertes + b.declarationsEnAttente - (a.anomaliesOuvertes + a.declarationsEnAttente) || a.nom.localeCompare(b.nom))
                  .slice(0, 80)
                  .map((i) => (
                    <tr key={i.id}>
                      <td>
                        <Link className="lien-ligne" to={`/stocks/installations/${i.id}`}>
                          {i.nom}
                        </Link>
                        <small>
                          {libelleCategorie(i.categorie)}, {i.organisationNom}
                        </small>
                      </td>
                      <td className="masquable">{libelleProvince(i.provinceCode)}</td>
                      <td>{i.fraicheur ? <PastilleFraicheur fraicheur={i.fraicheur} age={i.age ?? undefined} /> : <span className="attenue">aucune mesure</span>}</td>
                      <td className="nombre">
                        {i.anomaliesOuvertes + i.declarationsEnAttente > 0 ? (
                          <span className="etiquette ambre">{i.anomaliesOuvertes + i.declarationsEnAttente}</span>
                        ) : (
                          <span className="attenue">rien</span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="carte-bloc" aria-labelledby="titre-autres">
        <h2 id="titre-autres" className="sur-titre">
          Autres vues
        </h2>
        <ul className="liste-actions">
          <LienAction vers="/approvisionnements" icone="camion" titre="Arrivages et programmation" detail="Tableau à 7, 15, 30, 60 et 90 jours, cargaisons" />
          <LienAction vers="/gpl" icone="cuve" titre="Gaz butane (GPL)" detail="Vrac en kilogrammes, bouteilles par format" />
          <LienAction vers="/alertes" icone="incident" titre="Alertes" detail="Liste filtrable, actions, règles" />
        </ul>
      </section>
    </div>
  );
}
